import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FlaskConical, ArrowRight, AlertCircle } from 'lucide-react';

export default function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#0A1128] flex items-center justify-center px-6">
      {/* Grid overlay */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-12">
          <FlaskConical className="w-5 h-5 text-[#0A7C84]" strokeWidth={1.5} />
          <span className="text-lg tracking-tight text-white" style={{ fontFamily: 'var(--font-heading)', fontWeight: 700 }}>
            BIOLYN
          </span>
          <span className="text-[10px] tracking-widest text-white/30 uppercase ml-1" style={{ fontFamily: 'var(--font-mono)' }}>
            Admin Portal
          </span>
        </div>

        <h1
          className="text-3xl tracking-tight text-white mb-2"
          style={{ fontFamily: 'var(--font-heading)', fontWeight: 900 }}
          data-testid="admin-login-heading"
        >
          Sign In
        </h1>
        <p className="text-sm text-white/40 mb-8" style={{ fontFamily: 'var(--font-body)' }}>
          Authorized personnel only. Access the administration panel.
        </p>

        {error && (
          <div className="flex items-center gap-2 px-4 py-3 mb-6 border border-red-500/20 bg-red-500/5 text-red-400 text-sm" data-testid="admin-login-error">
            <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.5} />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} data-testid="admin-login-form">
          <div className="mb-4">
            <Label className="text-xs tracking-wider uppercase text-white/40 mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="rounded-none border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#0A7C84] focus:ring-[#0A7C84]"
              style={{ fontFamily: 'var(--font-body)' }}
              placeholder="admin@biolyn.com"
              data-testid="admin-login-email"
            />
          </div>
          <div className="mb-8">
            <Label className="text-xs tracking-wider uppercase text-white/40 mb-2 block" style={{ fontFamily: 'var(--font-mono)' }}>
              Password
            </Label>
            <Input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="rounded-none border-white/10 bg-white/5 text-white placeholder:text-white/20 focus:border-[#0A7C84] focus:ring-[#0A7C84]"
              style={{ fontFamily: 'var(--font-body)' }}
              placeholder="Enter password"
              data-testid="admin-login-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="group w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#0A7C84] text-white text-sm hover:bg-white hover:text-[#0A1128] transition-all duration-150 disabled:opacity-50"
            style={{ fontFamily: 'var(--font-body)', fontWeight: 500 }}
            data-testid="admin-login-submit"
          >
            {loading ? 'Signing in...' : 'Sign In'}
            <ArrowRight className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-1" strokeWidth={1.5} />
          </button>
        </form>

        <div className="mt-8 text-center">
          <a href="/" className="text-xs text-white/20 hover:text-white/40 transition-colors duration-150" style={{ fontFamily: 'var(--font-mono)' }}>
            Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
